import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, User, KeyRound, Mail, CheckCircle2, LockKeyhole } from "lucide-react";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { RegisterResponse } from "@/types/user";
import { Card, CardContent } from "@/components/ui/card";

interface AccountCreationProps {
  onAccountCreated: (userId: number, email: string, password: string) => void;
  userType?: "FULL" | "CONTRACTOR";
}

const formSchema = z
  .object({
    email: z.string().email("Invalid email address"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof formSchema>;

const AccountCreation: React.FC<AccountCreationProps> = ({
  onAccountCreated,
  userType = "FULL",
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const attemptLogin = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await ApiService.request<any>(
        EndpointKeys.LOGIN,
        {
          method: "POST",
          body: {
            email: email.toLowerCase().trim(),
            password,
          },
          skipAuth: true,
        },
        false,
      );
      
      // If login is successful, return true
      return !!response.key;
    } catch (error) {
      // Login failed, return false
      return false;
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      setError(undefined);

      // First try to login with the credentials
      const loginSuccessful = await attemptLogin(data.email, data.password);
      
      if (loginSuccessful) {
        // If login successful, get the user info
        const userResponse = await ApiService.request<any>(
          EndpointKeys.GET_USER_DETAILS,
          {
            method: "GET",
          }
        );
        
        toast({
          title: "Success",
          description: "Logged in with existing account",
        });
        
        // Call the callback with the user data
        onAccountCreated(userResponse.id, data.email, data.password);
        return;
      }

      // If login not successful, try to register
      try {
        const response = await ApiService.request<RegisterResponse>(
          EndpointKeys.SIGNUP,
          {
            method: "POST",
            body: {
              email: data.email,
              password1: data.password,
              password2: data.confirmPassword,
              user_type: userType,
            },
            skipAuth: true,
          },
          false,
        );

        // If we get here, either a new account was created or an existing account was authenticated
        // In both cases, we can proceed with the account
        onAccountCreated(response.user.id, data.email, data.password);
      } catch (signupError: any) {
        // Check if the error is about existing user with a different password
        // Django's IntegrityError for duplicate email can have various messages
        // TODO: Backend should be updated to catch IntegrityError and return 409 Conflict
        // instead of 500 Internal Server Error for email already exists cases
        const errorMessage = signupError.message || '';
        const isDuplicateEmailError = 
          errorMessage.includes("email already exists") || 
          errorMessage.includes("duplicate key value") || 
          errorMessage.includes("unique constraint") ||
          errorMessage.includes("already exists");
          
        if (isDuplicateEmailError) {
          // If email exists but login failed, the password must be incorrect
          setError("An account with this email already exists. Please use the correct password.");
          
          // Display a toast notification
          toast({
            title: "Account Exists",
            description: "Please enter the correct password for this email.",
          });
        } else {
          throw signupError;
        }
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create account";
      setError(message);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Determine appropriate text based on user type
  const getPageHeadingText = () => {
    return userType === "CONTRACTOR" ? "Create Your Contractor Account" : "Create Your Account";
  };

  const getPageSubtitle = () => {
    return userType === "CONTRACTOR" 
      ? "Set up your account to continue with your subscription and begin managing your contractor profile"
      : "Set up your account to continue with your subscription and begin managing your nanny payroll";
  };

  return (
    <div className="space-y-8">
      {/* Hero section with themed background */}
      <div className="bg-secondary text-secondary-foreground rounded-lg p-6 shadow-lg text-center">
        <div className="inline-flex items-center justify-center bg-secondary-foreground/10 p-3 rounded-full w-16 h-16 mb-4">
          <User className="h-8 w-8" />
        </div>
        <h1 className="text-3xl font-bold mb-2">{getPageHeadingText()}</h1>
        <p className="opacity-90 max-w-md mx-auto">
          {getPageSubtitle()}
        </p>
      </div>

      {/* Security benefits highlighting */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
        <div className="bg-accent p-4 rounded-lg text-center flex flex-col items-center">
          <div className="bg-accent/50 p-2 rounded-full w-10 h-10 flex items-center justify-center mb-2">
            <LockKeyhole className="h-5 w-5 text-primary" />
          </div>
          <h3 className="font-medium text-sm text-primary">Secure Account</h3>
          <p className="text-xs text-muted-foreground">Your data is encrypted and protected</p>
        </div>
        
        <div className="bg-secondary/10 p-4 rounded-lg text-center flex flex-col items-center">
          <div className="bg-secondary/20 p-2 rounded-full w-10 h-10 flex items-center justify-center mb-2">
            <Mail className="h-5 w-5 text-secondary" />
          </div>
          <h3 className="font-medium text-sm text-primary">Email Updates</h3>
          <p className="text-xs text-muted-foreground">Stay informed about your payroll</p>
        </div>
        
        <div className="bg-accent p-4 rounded-lg text-center flex flex-col items-center">
          <div className="bg-accent/50 p-2 rounded-full w-10 h-10 flex items-center justify-center mb-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
          </div>
          <h3 className="font-medium text-sm text-primary">Quick Setup</h3>
          <p className="text-xs text-muted-foreground">Just one step to complete your purchase</p>
        </div>
      </div>

      {/* Form card with accent styling */}
      <Card className="max-w-md mx-auto border-secondary/20 shadow-md">
        <CardContent className="pt-6">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary font-medium">Email Address</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          {...field}
                          type="email"
                          placeholder="you@example.com"
                          disabled={isLoading}
                          className="pl-10"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary font-medium">Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input 
                          {...field} 
                          type="password" 
                          disabled={isLoading}
                          className="pl-10" 
                        />
                      </div>
                    </FormControl>
                    <FormDescription className="text-xs">
                      Must be at least 8 characters and include uppercase,
                      lowercase, and numbers
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary font-medium">Confirm Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input 
                          {...field} 
                          type="password" 
                          disabled={isLoading}
                          className="pl-10" 
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button 
                type="submit" 
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 py-6 mt-4" 
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Create Account & Continue"
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
      
      {/* Trust indicators */}
      <div className="text-center text-sm text-muted-foreground max-w-md mx-auto">
        <p>By creating an account, you agree to our Terms of Service and Privacy Policy. Your data is securely stored and never shared with third parties.</p>
      </div>
    </div>
  );
};

export default AccountCreation;
